import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

// Query keys for consistent caching
export const queryKeys = {
  products: (filters = {}) => ['products', filters],
  product: (id) => ['product', id],
  categories: () => ['categories'],
  wishlist: () => ['wishlist'],
  cart: () => ['cart'],
  reviews: (productId) => ['reviews', productId],
  relatedProducts: (productId) => ['relatedProducts', productId],
  featuredProducts: () => ['featuredProducts'],
  recentlyViewed: () => ['recentlyViewed'],
  filterOptions: () => ['filterOptions']
};

// Products API hooks
export const useProducts = (filters = {}, page = 1) => {
  return useQuery({
    queryKey: queryKeys.products({ ...filters, page }),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== false && value !== null && value !== undefined) {
          const keyMap = {
            minPrice: 'min_price',
            maxPrice: 'max_price',
            minRating: 'min_rating',
            inStock: 'in_stock'
          };
          const backendKey = keyMap[key] || key;
          params.append(backendKey, String(value));
        }
      });

      const response = await axios.get(`/api/products?${params}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true, // Keep previous data while loading new data
  });
};

export const useProduct = (productId) => {
  return useQuery({
    queryKey: queryKeys.product(productId),
    queryFn: async () => {
      const response = await axios.get(`/api/products/${productId}`);
      return response.data.product;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    enabled: !!productId,
  });
};

export const useCategories = () => {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: async () => {
      const response = await axios.get('/api/categories');
      return response.data.categories || response.data.allCategories || response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - categories don't change often
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};

export const useWishlist = () => {
  return useQuery({
    queryKey: queryKeys.wishlist(),
    queryFn: async () => {
      try {
        const response = await axios.get('/api/wishlist');
        // Ensure we always return an array
        return Array.isArray(response.data) ? response.data : (response.data?.items || []);
      } catch (error) {
        // If user is not authenticated or other error, return empty array
        return [];
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on auth errors
  });
};

export const useProductReviews = (productId) => {
  return useQuery({
    queryKey: queryKeys.reviews(productId),
    queryFn: async () => {
      const response = await axios.get(`/api/products/${productId}/reviews`);
      return response.data.reviews || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!productId,
  });
};

export const useRelatedProducts = (productId) => {
  return useQuery({
    queryKey: queryKeys.relatedProducts(productId),
    queryFn: async () => {
      const response = await axios.get(`/api/products/${productId}/related`);
      return response.data.products || [];
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    enabled: !!productId,
  });
};

export const useFeaturedProducts = () => {
  return useQuery({
    queryKey: queryKeys.featuredProducts(),
    queryFn: async () => {
      const response = await axios.get('/api/products/featured/list');
      return response.data.products || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const useFilterOptions = () => {
  return useQuery({
    queryKey: queryKeys.filterOptions(),
    queryFn: async () => {
      const response = await axios.get('/api/products/filters');
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};

// Mutations for data updates
export const useAddToWishlist = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (productId) => {
      const response = await axios.post('/api/wishlist/add', { product_id: productId });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate wishlist cache
      queryClient.invalidateQueries({ queryKey: queryKeys.wishlist() });
    },
  });
};

export const useRemoveFromWishlist = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (productId) => {
      const response = await axios.delete(`/api/wishlist/remove/${productId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate wishlist cache
      queryClient.invalidateQueries({ queryKey: queryKeys.wishlist() });
    },
  });
};

export const useAddToCart = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ productId, quantity = 1, selectedOptions = {} }) => {
      const sessionId = localStorage.getItem('cart_session_id');
      const response = await axios.post('/api/cart/add', {
        product_id: productId,
        quantity,
        selected_options: selectedOptions
      }, {
        headers: {
          'x-session-id': sessionId
        }
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate cart cache
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
};

// Prefetch utilities for better UX
export const usePrefetch = () => {
  const queryClient = useQueryClient();

  const prefetchProduct = (productId) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.product(productId),
      queryFn: async () => {
        const response = await axios.get(`/api/products/${productId}`);
        return response.data.product;
      },
      staleTime: 10 * 60 * 1000,
    });
  };

  const prefetchRelatedProducts = (productId) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.relatedProducts(productId),
      queryFn: async () => {
        const response = await axios.get(`/api/products/${productId}/related`);
        return response.data.products || [];
      },
      staleTime: 15 * 60 * 1000,
    });
  };

  return { prefetchProduct, prefetchRelatedProducts };
};
